import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Package,
  ChevronRight,
  Image as ImageIcon,
  CheckCircle,
  Grid,
  List,
  Home,
  Layers,
  ArrowRight
} from 'lucide-react';
import { useCategory, useRecordActivity, useActivityTimer,  useHierarchicalProgress, useUserActivities } from '../../hooks';
import { Card, LoadingSpinner, Button, Badge, Modal, ImageWithLoader } from '../../components/common';
import { categoryService, subcategoryService, SubcategoryHierarchy } from '../../services/api';
import api from '../../services/api';


import QuickUserActions from '@/components/users/QuickUserActions';


interface BreadcrumbItem {
  id: string;
  name: string;
  level: number;
}

const PortfolioPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { data: categoryData, isLoading } = useCategory(categoryId!);
  const recordActivity = useRecordActivity();
  const { getElapsedSeconds } = useActivityTimer();

  // Estados para navegación jerárquica
  const [currentSubcategories, setCurrentSubcategories] = useState<SubcategoryHierarchy[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [currentLevel, setCurrentLevel] = useState<number>(0);
  const [isLoadingSubcategories, setIsLoadingSubcategories] = useState(false);

  // Estados para productos
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [viewedProducts, setViewedProducts] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [hasAutoCompleted, setHasAutoCompleted] = useState(false);

  const category = categoryData?.category;

  const { data: activitiesData } = useUserActivities({ categoryId: categoryId! });
  
    const progress = useHierarchicalProgress(
      categoryData?.category?.subcategories || [],
      activitiesData?.users || []
    );
  

  const API_ORIGIN: string = (() => {
    try {
      const base = (api?.defaults?.baseURL as string) || '';
      return base ? new URL(base).origin : window.location.origin;
    } catch {
      return window.location.origin;
    }
  })();

  // Si la URL viene relativa (/uploads/...), la convierte a absoluta con el backend.
  const resolveImageUrl = (u?: string) => {
    if (!u) return '';
    if (/^https?:\/\//i.test(u)) return u;       // ya es absoluta
    if (u.startsWith('/uploads/')) return `${API_ORIGIN}${u}`;
    return u;                                     // cualquier otra ruta, déjala igual
  };

  // === NUEVO: icono para categoría (iconUrl o icon_url)
  const getCategoryIconUrl = (cat: any): string => {
    const raw = cat?.iconUrl ?? cat?.icon_url ?? '';
    return resolveImageUrl(raw || '');
  };

  // === NUEVO: icono para subcategoría (iconUrl o icon_url)
  const getSubcategoryIconUrl = (sub: any): string => {
    const raw = sub?.iconUrl ?? sub?.icon_url ?? '';
    return resolveImageUrl(raw || '');
  };

  // Inicializar con subcategorías raíz
  useEffect(() => {
    if (category?.subcategories) {
      setCurrentSubcategories(category.subcategories);
      setBreadcrumbs([]);
      setCurrentLevel(0);
    }
  }, [category]);

  // Calcular progreso total
  const getTotalProducts = () => {
    const countProductsRecursively = (subcategories: SubcategoryHierarchy[]): number => {
      return subcategories.reduce((total, sub) => {
        let count = sub.products?.length || 0;
        if (sub.children) {
          count += countProductsRecursively(sub.children);
        }
        return total + count;
      }, 0);
    };

    return countProductsRecursively(category?.subcategories || []);
  };

  const totalProducts = getTotalProducts();
  const progressPercentage = totalProducts > 0 ? (viewedProducts.size / totalProducts) * 100 : 0;

  // AUTOCOMPLETADO
  useEffect(() => {
    const shouldAutoComplete = () => {
      const threshold = totalProducts <= 3 ? 1 : Math.ceil(totalProducts * 0.8);
      return viewedProducts.size >= threshold;
    };

    if (!hasAutoCompleted &&
      totalProducts > 0 &&
      shouldAutoComplete() &&
      !recordActivity.isPending) {

      markAsCompleted();
      setHasAutoCompleted(true);
    }
  }, [viewedProducts, totalProducts, hasAutoCompleted, recordActivity.isPending]);

  // Navegar hacia una subcategoría
  const navigateToSubcategory = async (subcategory: SubcategoryHierarchy) => {
    if (subcategory.hasProducts && !subcategory.hasChildren) {
      return;
    }

    if (subcategory.hasChildren) {
      setIsLoadingSubcategories(true);

      try {
        const response = await subcategoryService.getChildren(subcategory.id);

        setCurrentSubcategories(response.subcategories);

        const newBreadcrumbs = [
          ...breadcrumbs,
          {
            id: subcategory.id,
            name: subcategory.name,
            level: currentLevel
          }
        ];
        setBreadcrumbs(newBreadcrumbs);
        setCurrentLevel(currentLevel + 1);

      } catch (error) {
        console.error('Error navegando a subcategoría:', error);
      } finally {
        setIsLoadingSubcategories(false);
      }
    }
  };

  // Navegar hacia atrás en el breadcrumb
  const navigateToBreadcrumb = async (targetIndex: number) => {
    if (targetIndex === -1) {
      setCurrentSubcategories(category?.subcategories || []);
      setBreadcrumbs([]);
      setCurrentLevel(0);
      return;
    }

    const targetBreadcrumb = breadcrumbs[targetIndex];
    setIsLoadingSubcategories(true);

    try {
      const response = await subcategoryService.getChildren(targetBreadcrumb.id);
      setCurrentSubcategories(response.subcategories);

      setBreadcrumbs(breadcrumbs.slice(0, targetIndex + 1));
      setCurrentLevel(targetBreadcrumb.level + 1);

    } catch (error) {
      console.error('Error navegando por breadcrumb:', error);
    } finally {
      setIsLoadingSubcategories(false);
    }
  };

  // Manejar visualización de producto
  const handleProductView = async (product: any) => {
    setSelectedProduct(product);
    const newViewedProducts = new Set(viewedProducts);
    newViewedProducts.add(product.id);
    setViewedProducts(newViewedProducts);
  };

  const markAsCompleted = async () => {
    if (hasAutoCompleted) return;

    try {
      await recordActivity.mutateAsync({
        categoryId: categoryId!,
        activityType: 'portfolio',
        completed: true,
        timeSpent: getElapsedSeconds(),
        metadata: {
          viewedProducts: Array.from(viewedProducts),
          totalProductsViewed: viewedProducts.size,
          totalProducts: getTotalProducts(),
          maxLevelReached: currentLevel,
          autoCompleted: true,
          completionThreshold: totalProducts <= 3 ? 1 : Math.ceil(totalProducts * 0.8),
          allProductsData: Array.from(viewedProducts).map(productId => ({ productId }))
        }
      });
    } catch (error) {
      console.error('Error marcando portfolio como completado:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-brinsa-azul mb-4">
          Categoría no encontrada
        </h2>
        <Link to="/home">
          <Button variant="outline">
            Volver a categorías
          </Button>
        </Link>
      </div>
    );
  }

  // URL del icono de la categoría (si existe)
  const categoryIconUrl = getCategoryIconUrl(category);

  return (
    <div className="space-y-6">
      {/* Modal de producto individual */}
      <Modal
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        title={selectedProduct?.name}
        size="lg"
      >
        {selectedProduct && (
          <div className="space-y-4">
            {selectedProduct.imageUrl ? (
              <ImageWithLoader
                src={resolveImageUrl(selectedProduct.imageUrl)}
                alt={selectedProduct.name}
                className="w-full h-64 object-contain rounded-xl border border-brinsa-celeste/30 bg-gray-50"
                fallbackClassName="w-full h-64"
              />
            ) : (
              <div className="w-full h-64 bg-brinsa-celeste/10 rounded-xl border border-brinsa-celeste/30 flex items-center justify-center">
                <div className="text-center">
                  <ImageIcon className="w-16 h-16 text-brinsa-azul/60 mx-auto mb-2" />
                  <p className="text-sm text-brinsa-azulOscuro/60">Sin imagen disponible</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-brinsa-azul mb-2">Información del producto</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-brinsa-azulOscuro/70">Nombre:</span>
                    <span className="ml-2 font-medium text-brinsa-azul">{selectedProduct.name}</span>
                  </div>
                  {selectedProduct.internalCode && (
                    <div>
                      <span className="text-brinsa-azulOscuro/70">EAN:</span>
                      <span className="ml-2 font-medium font-mono bg-brinsa-celeste/10 px-2 py-1 rounded border border-brinsa-celeste/30">
                        {selectedProduct.internalCode}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="text-brinsa-azulOscuro/70">Presentación:</span>
                    <span className="ml-2 font-medium text-brinsa-azul">{selectedProduct.subcategory?.size}</span>
                  </div>
                  <div>
                    <span className="text-brinsa-azulOscuro/70">Ruta:</span>
                    <span className="ml-2 font-medium text-xs text-brinsa-azul">
                      {category.name} {breadcrumbs.length > 0 && ' > ' + breadcrumbs.map(b => b.name).join(' > ')}
                    </span>
                  </div>
                </div>
              </div>

              {selectedProduct.description && (
                <div>
                  <h4 className="font-semibold text-brinsa-azul mb-2">Descripción</h4>
                  <p className="text-sm text-brinsa-azulOscuro/80 whitespace-pre-line">
                    {selectedProduct.description.replace(/\\n/g, '\n -')}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-center">
              <Badge variant="success" size="sm">
                <CheckCircle className="w-3 h-3 mr-1" />
                Producto visualizado
              </Badge>
            </div>
          </div>
        )}
      </Modal>

      {/* Navegación */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link
          to={`/category/${categoryId}`}
          className="inline-flex items-center text-brinsa-azulOscuro/80 hover:text-brinsa-azul transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a {category.name}
        </Link>
      </motion.div>

      {/* Header con breadcrumbs e icono de categoría */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="space-y-4"
      >
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-brinsa-celeste/20 border border-brinsa-celeste/30 grid place-items-center overflow-hidden">
              {categoryIconUrl ? (
                <img
                  src={categoryIconUrl}
                  alt={category.name}
                  className="w-8 h-8 object-contain"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLDivElement | null;
                    if (fallback) fallback.style.display = 'grid';
                  }}
                />
              ) : null}
              <div className="w-full h-full hidden place-items-center">
                <Package className="w-6 h-6 text-brinsa-azul" />
              </div>
              {!categoryIconUrl && <Package className="w-6 h-6 text-brinsa-azul" />}
            </div>

            <h1 className="text-3xl font-bold text-brinsa-azul">
              Portafolio: {category.name}
            </h1>
          </div>
          <p className="text-lg text-brinsa-azulOscuro/80">
            Explora todos nuestros productos y sus diferentes presentaciones
          </p>
        </div>

        {/* Breadcrumbs de navegación */}
        {breadcrumbs.length > 0 && (
          <nav className="flex items-center space-x-2 text-sm bg-brinsa-celeste/10 rounded-lg px-3 py-2 border border-brinsa-celeste/30">
            <button
              onClick={() => navigateToBreadcrumb(-1)}
              className="flex items-center text-brinsa-azul hover:text-brinsa-azul/80 transition-colors"
            >
              <Home className="w-4 h-4 mr-1" />
              {category.name}
            </button>

            {breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={breadcrumb.id}>
                <ArrowRight className="w-4 h-4 text-brinsa-azul/40" />
                <button
                  onClick={() => navigateToBreadcrumb(index)}
                  className={`transition-colors ${index === breadcrumbs.length - 1
                    ? 'text-brinsa-azul font-medium'
                    : 'text-brinsa-azul hover:text-brinsa-azul/80'
                    }`}
                >
                  {breadcrumb.name}
                </button>
              </React.Fragment>
            ))}
          </nav>
        )}
      </motion.div>

      {/* Progreso global */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <Card className="border border-brinsa-celeste/30">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-brinsa-azul flex items-center">
                <Layers className="w-4 h-4 mr-2" />
                Productos explorados globalmente
              </span>
              <span className="text-sm text-brinsa-azulOscuro/80">
                {viewedProducts.size}/{totalProducts} productos
              </span>
            </div>
            <div className="w-full bg-brinsa-celeste/20 rounded-full h-2">
              <div
                className="bg-brinsa-verde h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-brinsa-azulOscuro/70">
                {Math.round(progressPercentage)}% completado • Nivel {currentLevel}
              </span>

              {hasAutoCompleted && (
                <Badge variant="success" size="sm">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Completado automáticamente
                </Badge>
              )}

              {!hasAutoCompleted && totalProducts > 0 && (
                <span className="text-xs text-brinsa-azul">
                  {totalProducts <= 3
                    ? `Falta ${Math.max(0, 1 - viewedProducts.size)} producto`
                    : `Faltan ${Math.max(0, Math.ceil(totalProducts * 0.8) - viewedProducts.size)} productos (80%)`
                  }
                </span>
              )}
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Loading subcategorías */}
      {isLoadingSubcategories && (
        <div className="flex justify-center items-center py-8">
          <LoadingSpinner size="md" />
          <span className="ml-2 text-brinsa-azulOscuro/80">Cargando subcategorías...</span>
        </div>
      )}

      {/* Grid de subcategorías/productos actuales */}
      {!isLoadingSubcategories && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-4"
        >
          {/* Controles de vista */}
          {currentSubcategories.some(sub => sub.hasProducts) && (
            <div className="flex justify-end">
              <div className="flex items-center space-x-2 bg-brinsa-celeste/10 border border-brinsa-celeste/30 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors ${viewMode === 'grid'
                    ? 'bg-white text-brinsa-azul shadow-sm border border-brinsa-celeste/40'
                    : 'text-brinsa-azulOscuro/70 hover:text-brinsa-azul'
                    }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${viewMode === 'list'
                    ? 'bg-white text-brinsa-azul shadow-sm border border-brinsa-celeste/40'
                    : 'text-brinsa-azulOscuro/70 hover:text-brinsa-azul'
                    }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          <div className={`grid gap-6 ${viewMode === 'grid'
            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            : 'grid-cols-1'
            }`}>
            {currentSubcategories.map((subcategory, index) => (
              <motion.div
                key={subcategory.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <SubcategoryCard
                  subcategory={subcategory}
                  viewMode={viewMode}
                  onNavigate={navigateToSubcategory}
                  onProductView={handleProductView}
                  viewedProducts={viewedProducts}
                  // NUEVO: pasamos el resolver de icono
                  getSubcategoryIconUrl={getSubcategoryIconUrl}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Estado vacío */}
      {!isLoadingSubcategories && currentSubcategories.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-brinsa-azul/30 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-brinsa-azul mb-2">
            No hay contenido disponible
          </h3>
          <p className="text-brinsa-azulOscuro/80">
            {breadcrumbs.length > 0
              ? 'Esta subcategoría no tiene productos o subcategorías configuradas.'
              : 'Esta categoría no tiene productos configurados aún.'
            }
          </p>
        </div>
      )}
      <QuickUserActions progress={progress}/>
    </div>
  );
};

// Componente para cada tarjeta de subcategoría
interface SubcategoryCardProps {
  subcategory: SubcategoryHierarchy;
  viewMode: 'grid' | 'list';
  onNavigate: (subcategory: SubcategoryHierarchy) => void;
  onProductView: (product: any) => void;
  viewedProducts: Set<string>;
  getSubcategoryIconUrl: (sub: any) => string;
}

const SubcategoryCard: React.FC<SubcategoryCardProps> = ({
  subcategory,
  viewMode,
  onNavigate,
  onProductView,
  viewedProducts,
  getSubcategoryIconUrl
}) => {
  // NUEVO: Estado para controlar expansión de productos
  const [showAllProducts, setShowAllProducts] = useState(false);

  const hasNavigableChildren = subcategory.hasChildren;
  const hasViewableProducts = subcategory.hasProducts && !subcategory.hasChildren;

  const subIconUrl = getSubcategoryIconUrl(subcategory);
  const FallbackIcon = hasNavigableChildren ? Layers : Package;

  // NUEVO: Determinar productos a mostrar
  const productsToShow = showAllProducts
    ? subcategory.products
    : subcategory.products?.slice(0, 3);

  return (
    <Card className="h-full hover:shadow-lg transition-all duration-300 border border-brinsa-celeste/30">
      <div className="p-6">
        {/* Header de la tarjeta con icono/imagen */}
        <div className="flex items-center justify-between mb-4">
          <div className="w-12 h-12 bg-brinsa-celeste/30 rounded-xl grid place-items-center overflow-hidden border border-brinsa-celeste/40">
            {subIconUrl ? (
              <img
                src={subIconUrl}
                alt={subcategory.name}
                className="w-8 h-8 object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLDivElement | null;
                  if (fallback) fallback.style.display = 'grid';
                }}
              />
            ) : null}
            <div className="w-full h-full hidden place-items-center">
              <FallbackIcon className="w-6 h-6 text-brinsa-azul" />
            </div>
            {!subIconUrl && <FallbackIcon className="w-6 h-6 text-brinsa-azul" />}
          </div>

          <div className="flex space-x-2">
            {hasNavigableChildren && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brinsa-celeste/20 text-brinsa-azul">
                {subcategory.children?.length || subcategory.childrenCount || 0} subcategorías
              </span>
            )}
            {subcategory.hasProducts && (
              <Badge variant="success" size="sm">
                {subcategory.productCount} productos
              </Badge>
            )}
          </div>
        </div>

        {/* Título y descripción */}
        <h3 className="text-lg font-semibold text-brinsa-azul mb-2">
          {subcategory.name}
        </h3>
        <p className="text-brinsa-azulOscuro/80 mb-4">
          Presentación: {subcategory.size}
        </p>

        {/* Preview de contenido */}
        <div className="space-y-2 mb-4">
          {hasNavigableChildren && (
            <>
              <h4 className="text-sm font-medium text-brinsa-azul mb-2">Subcategorías:</h4>
              <div className="text-xs text-brinsa-azulOscuro/80 bg-brinsa-celeste/10 border border-brinsa-celeste/30 p-2 rounded">
                Contiene {subcategory.childrenCount || subcategory.children?.length || 0} subcategorías más
              </div>
            </>
          )}

          {hasViewableProducts && subcategory.products && (
            <>
              <h4 className="text-sm font-medium text-brinsa-azul mb-2">Productos:</h4>

              {/* NUEVO: Contenedor con scroll para productos */}
              <div className={`space-y-2 ${showAllProducts ? 'max-h-64 overflow-y-auto' : ''}`}>
                {productsToShow?.map((product) => (
                  <div
                    key={product.id}
                    className="flex items-center justify-between p-2 bg-brinsa-celeste/10 border border-brinsa-celeste/30 rounded text-sm cursor-pointer hover:bg-brinsa-celeste/20 transition-colors"
                    onClick={() => onProductView(product)}
                  >
                    <span className="truncate text-brinsa-azul">{product.name}</span>
                    <div className="flex items-center space-x-2">
                      {viewedProducts.has(product.id) && (
                        <CheckCircle className="w-4 h-4 text-brinsa-verde" />
                      )}
                      <ChevronRight className="w-4 h-4 text-brinsa-azul/60" />
                    </div>
                  </div>
                ))}
              </div>

              {/* NUEVO: Botón Ver todos/Ver menos */}
              {subcategory.products.length > 3 && (
                <button
                  onClick={() => setShowAllProducts(!showAllProducts)}
                  className="w-full mt-2 py-2 px-3 text-xs font-medium text-brinsa-azul bg-brinsa-celeste/10 hover:bg-brinsa-celeste/20 border border-brinsa-celeste/30 rounded transition-colors flex items-center justify-center"
                >
                  {showAllProducts ? (
                    <>
                      <ChevronRight className="w-3 h-3 mr-1 rotate-90 transition-transform" />
                      Ver menos
                    </>
                  ) : (
                    <>
                      <ChevronRight className="w-3 h-3 mr-1 -rotate-90 transition-transform" />
                      Ver todos ({subcategory.products.length} productos)
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>

        {/* Botón de acción */}
        <div className="space-y-2">
          {hasNavigableChildren && (
            <Button
              variant="primary"
              className="w-full bg-brinsa-azul hover:bg-brinsa-azul/90 text-white"
              onClick={() => onNavigate(subcategory)}
            >
              <Layers className="w-4 h-4 mr-2" />
              Explorar subcategorías
            </Button>
          )}

          {hasViewableProducts && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-brinsa-azulOscuro/80">
                <span>
                  {subcategory.products?.filter(p => viewedProducts.has(p.id)).length || 0}/
                  {subcategory.products?.length || 0} productos vistos
                </span>
                <span>
                  {Math.round(((subcategory.products?.filter(p => viewedProducts.has(p.id)).length || 0) /
                    (subcategory.products?.length || 1)) * 100)}%
                </span>
              </div>

              <div className="w-full bg-brinsa-celeste/20 rounded-full h-1">
                <div
                  className="bg-brinsa-verde h-1 rounded-full transition-all duration-300"
                  style={{
                    width: `${((subcategory.products?.filter(p => viewedProducts.has(p.id)).length || 0) /
                      (subcategory.products?.length || 1)) * 100}%`
                  }}
                />
              </div>
            </div>
          )}

          {!hasNavigableChildren && !hasViewableProducts && (
            <div className="text-center py-4">
              <Package className="w-8 h-8 text-brinsa-azul/30 mx-auto mb-2" />
              <p className="text-sm text-brinsa-azulOscuro/70">Sin contenido disponible</p>
            </div>
          )}
        </div>
      </div>
      
    </Card>
    
  );
};

export default PortfolioPage;
